import React, { useEffect, useRef, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  TextInput,
  SelectInput,
  DateInput,
  ReferenceInput,
  TopToolbar,
  useListContext,
  useNotify,
  useTranslate,
  RecordContextProvider,
} from 'react-admin';
import { Box, Button, Chip, TextField as MuiTextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../../config/api';
import { tokenManager } from '../../../utils/tokenManager';
import { apiClient } from '../../../utils/apiClient';
import { KIOSK_PAYMENT_METHODS, REFUND_STATE_FILTERS, exportQuery, paidWith, refundStateColor, settleStateColor } from './refundState';
import type { KioskTransaction } from './types';

const money = (value: number | null) => (value === null ? '-' : `HK$${value.toFixed(2)}`);
const CARD_LOOKUP_DEBOUNCE_MS = 400;

interface OctopusCardSearchFilterProps {
  source?: string;
  alwaysOn?: boolean;
}

export const OctopusCardSearchFilter = (_props: OctopusCardSearchFilterProps) => {
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const filterValuesRef = useRef(filterValues);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    filterValuesRef.current = filterValues;
  }, [filterValues]);

  useEffect(() => {
    const trimmed = value.trim();
    const mySeq = ++requestSeqRef.current;
    const clearCardHash = () => {
      if (filterValuesRef.current.cardHash !== undefined) {
        const { cardHash, ...rest } = filterValuesRef.current;
        setFilters(rest, undefined, false);
      }
    };

    if (trimmed === '') {
      setError(null);
      clearCardHash();
      return;
    }
    const timeout = setTimeout(() => {
      setError(null);
      clearCardHash();
      apiClient
        .post<{ data: { cardHash: string } }>('/api/backend/kiosk-transactions/card-lookup', { octopusCardId: trimmed })
        .then((response) => {
          if (requestSeqRef.current !== mySeq) return;
          setFilters({ ...filterValuesRef.current, cardHash: response.data.cardHash }, undefined, false);
        })
        .catch(() => {
          if (requestSeqRef.current !== mySeq) return;
          setError(translate('resources.kiosk-transactions.filters.octopusCardIdInvalid'));
        });
    }, CARD_LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <MuiTextField
      label={translate('resources.kiosk-transactions.filters.octopusCardId')}
      helperText={error ?? translate('resources.kiosk-transactions.filters.octopusCardIdHelper')}
      error={error !== null}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      inputProps={{ 'data-testid': 'octopus-card-search' }}
      size="small"
      variant="filled"
    />
  );
};

export const kioskTransactionFilters = (translate: (key: string, options?: any) => string) => [
  <TextInput key="receiptReference" source="receiptReference" label={translate('resources.kiosk-transactions.filters.receiptReference')} alwaysOn />,
  <OctopusCardSearchFilter key="octopusCardId" source="octopusCardId" alwaysOn />,
  <ReferenceInput key="siteId" source="siteId" reference="sites">
    <SelectInput optionText="name" label={translate('resources.kiosk-transactions.filters.siteId')} />
  </ReferenceInput>,
  <SelectInput
    key="method"
    source="method"
    label={translate('resources.kiosk-transactions.filters.method')}
    choices={KIOSK_PAYMENT_METHODS.map((id) => ({ id, name: translate(`resources.kiosk-transactions.methods.${id}`) }))}
  />,
  <SelectInput
    key="refundState"
    source="refundState"
    label={translate('resources.kiosk-transactions.filters.refundState')}
    choices={REFUND_STATE_FILTERS.map((id) => ({ id, name: translate(`resources.kiosk-transactions.refundStates.${id}`) }))}
  />,
  <DateInput key="from" source="from" label={translate('resources.kiosk-transactions.filters.from')} />,
  <DateInput key="to" source="to" label={translate('resources.kiosk-transactions.filters.to')} />,
];

export const RefundStateChip = ({ state }: { state: string }) => {
  const translate = useTranslate();
  return <Chip size="small" color={refundStateColor(state)} label={translate(`resources.kiosk-transactions.refundStates.${state}`)} />;
};

export const SettleStateChip = ({ state }: { state: KioskTransaction['settleState'] }) => {
  const translate = useTranslate();
  if (state === 'none') return <>-</>;
  return <Chip size="small" variant="outlined" color={settleStateColor(state)} label={translate(`resources.kiosk-transactions.settleStates.${state}`)} />;
};

const ExportButton = () => {
  const { filterValues } = useListContext();
  const notify = useNotify();
  const translate = useTranslate();
  const handleExport = async () => {
    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/kiosk-transactions/export${exportQuery(filterValues)}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!response.ok) throw new Error(`Export failed with ${response.status}`);
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kiosk-transactions.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notify(translate('resources.kiosk-transactions.messages.exportFailed'), { type: 'error' });
    }
  };
  return (
    <TopToolbar>
      <Button startIcon={<DownloadIcon />} onClick={handleExport} variant="contained" size="small">
        {translate('resources.kiosk-transactions.actions.export')}
      </Button>
    </TopToolbar>
  );
};

const MobileList = () => {
  const { data, isLoading } = useListContext<KioskTransaction>();
  const navigate = useNavigate();
  const translate = useTranslate();
  if (isLoading || !data) return null;
  return (
    <Box>
      {data.map((record) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box onClick={() => navigate(`/kiosk-transactions/${record.id}/show`)} sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{record.receiptReference}</Typography>
              <RefundStateChip state={record.refundState} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {paidWith(record, translate) ?? translate(`resources.kiosk-transactions.methods.${record.method}`)} · {record.siteName} · {money(record.refundAmount)} · {new Date(record.createdAt).toLocaleString()}
            </Typography>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const KioskTransactionsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <List
      resource="kiosk-transactions"
      filters={kioskTransactionFilters(translate)}
      actions={<ExportButton />}
      perPage={25}
      sort={{ field: 'createdAt', order: 'DESC' }}
      disableSyncWithLocation
      title={translate('resources.kiosk-transactions.name')}
    >
      {isMobile ? (
        <MobileList />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="show">
          <DateField source="createdAt" label={translate('resources.kiosk-transactions.fields.createdAt')} showTime />
          <TextField source="receiptReference" label={translate('resources.kiosk-transactions.fields.receiptReference')} sortable={false} />
          <TextField source="siteName" label={translate('resources.kiosk-transactions.fields.siteName')} sortable={false} />
          <TextField source="kioskName" label={translate('resources.kiosk-transactions.fields.kioskName')} sortable={false} />
          <FunctionField
            label={translate('resources.kiosk-transactions.fields.method')}
            render={(record: KioskTransaction) => translate(`resources.kiosk-transactions.methods.${record.method}`)}
          />
          <FunctionField
            label={translate('resources.kiosk-transactions.fields.paidWith')}
            render={(record: KioskTransaction) => paidWith(record, translate) ?? '-'}
          />
          <FunctionField source="depositAmount" label={translate('resources.kiosk-transactions.fields.depositAmount')} render={(record: KioskTransaction) => money(record.depositAmount)} />
          <FunctionField label={translate('resources.kiosk-transactions.fields.finalCharge')} render={(record: KioskTransaction) => money(record.finalCharge)} />
          <FunctionField source="refundAmount" label={translate('resources.kiosk-transactions.fields.refundAmount')} render={(record: KioskTransaction) => money(record.refundAmount)} />
          <FunctionField label={translate('resources.kiosk-transactions.fields.refundState')} render={(record: KioskTransaction) => <RefundStateChip state={record.refundState} />} />
          <FunctionField label={translate('resources.kiosk-transactions.fields.settleState')} render={(record: KioskTransaction) => <SettleStateChip state={record.settleState} />} />
        </Datagrid>
      )}
    </List>
  );
};

export default KioskTransactionsList;
